import { supabase } from './supabase';
import { API_ORIGIN } from './platform';

const API_BASE = `${API_ORIGIN}/api`;

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = {};
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

// Reads are remembered so moving between screens doesn't refetch what was just
// loaded. A cached answer is reused for up to MAX_AGE; once it's older than
// REFRESH_AFTER it's still returned, and a fresh copy is fetched in the
// background for next time. Any write clears everything.
const REFRESH_AFTER = 30 * 1000;
const MAX_AGE = 5 * 60 * 1000;
const cache = new Map(); // path -> { at, data }
const inflight = new Map(); // path -> promise

export function clearApiCache() {
  cache.clear();
  inflight.clear();
}

async function requestJSON(path) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

function load(path) {
  if (!inflight.has(path)) {
    const p = requestJSON(path)
      .then((data) => { cache.set(path, { at: Date.now(), data }); return data; })
      .finally(() => inflight.delete(path));
    inflight.set(path, p);
  }
  return inflight.get(path);
}

async function fetchJSON(path) {
  const hit = cache.get(path);
  const age = hit ? Date.now() - hit.at : Infinity;
  if (age < MAX_AGE) {
    if (age > REFRESH_AFTER) load(path).catch(() => {});
    return hit.data;
  }
  return load(path);
}

async function patchJSON(path, body) {
  clearApiCache();
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function postJSON(path, body) {
  clearApiCache();
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...authHeaders },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `API error: ${res.status}` }));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

async function deleteJSON(path) {
  clearApiCache();
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, { method: 'DELETE', headers: authHeaders });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

// Load a clinic's page data ahead of a likely tap, so it opens instantly
function prefetchClinic(id) {
  [`/clinics/${id}`, `/clinics/${id}/chart`, `/clinics/${id}/clinicians`]
    .forEach((path) => fetchJSON(path).catch(() => {}));
}

export const api = {
  prefetchClinic,
  dashboard: {
    chart: () => fetchJSON('/dashboard/chart'),
  },
  clinics: {
    list: () => fetchJSON('/clinics'),
    get: (id) => fetchJSON(`/clinics/${id}`),
    clinicians: (id) => fetchJSON(`/clinics/${id}/clinicians`),
    chart: (id) => fetchJSON(`/clinics/${id}/chart`),
    records: (id, month, year) => fetchJSON(`/clinics/${id}/records?month=${month}&year=${year}`),
    updateRecord: (clinicId, recordId, data) => patchJSON(`/clinics/${clinicId}/records/${recordId}`, data),
    updateClinician: (clinicId, clinicianId, data) => patchJSON(`/clinics/${clinicId}/clinicians/${clinicianId}`, data),
    recalculate: (id, month, year) => postJSON(`/clinics/${id}/recalculate?month=${month}&year=${year}`),
    range: (id, from, to) => fetchJSON(`/clinics/${id}/range?from=${from}&to=${to}`),
    trend: (id, from, to) => fetchJSON(`/clinics/${id}/trend?from=${from}&to=${to}`),
  },
  settings: {
    scoring: () => fetchJSON('/settings/scoring'),
    updateProfile: (firstName, lastName) => patchJSON('/settings/profile', { firstName, lastName }),
    updateEmail: (email) => patchJSON('/settings/email', { email }),
    updatePassword: (password) => patchJSON('/settings/password', { password }),
    deleteAccount: () => deleteJSON('/settings/account'),
    clinics: () => fetchJSON('/settings/clinics'),
    createClinic: (name, email, firstName, lastName) => postJSON('/settings/clinics', { name, email, firstName, lastName }),
    updateClinicName: (clinicId, name) => patchJSON(`/settings/clinics/${clinicId}`, { name }),
    invite: (clinicId, data) => postJSON(`/settings/clinics/${clinicId}/invite`, data),
    cancelInvite: (clinicId, inviteId) => deleteJSON(`/settings/clinics/${clinicId}/invite/${inviteId}`),
    removeMember: (clinicId, memberId) => deleteJSON(`/settings/clinics/${clinicId}/members/${memberId}`),
  },
  clinicians: {
    list: () => fetchJSON('/clinicians'),
    get: (id) => fetchJSON(`/clinicians/${id}`),
    chart: (id) => fetchJSON(`/clinicians/${id}/chart`),
    records: (id, month, year) => fetchJSON(`/clinicians/${id}/records?month=${month}&year=${year}`),
  },
  billing: {
    createCheckoutSession: (clinicId, plan, couponCode) => postJSON('/billing/create-checkout-session', { clinicId, plan, couponCode }),
    createPortalSession: (clinicId) => postJSON('/billing/create-portal-session', { clinicId }),
    subscription: (clinicId) => fetchJSON(`/billing/subscription/${clinicId}`),
  },
  hipaaVault: {
    sync: (clinicId) => postJSON(`/hipaa-vault/sync/${clinicId}`),
    syncAll: () => postJSON('/hipaa-vault/sync-all'),
    testConnection: (clinicId) => postJSON(`/hipaa-vault/test/${clinicId}`),
    syncs: (clinicId) => fetchJSON(`/hipaa-vault/syncs/${clinicId}`),
  },
  dailyCaseload: {
    sync: (clinicId, days) => postJSON(`/daily-caseload/sync/${clinicId}${days ? `?days=${days}` : ''}`),
    syncAll: () => postJSON('/daily-caseload/sync-all'),
    breakdown: (clinicId, month, year) => fetchJSON(`/daily-caseload/${clinicId}?month=${month}&year=${year}`),
  },
  dailyAttendance: {
    sync: (clinicId, days) => postJSON(`/daily-attendance/sync/${clinicId}${days ? `?days=${days}` : ''}`),
    syncAll: () => postJSON('/daily-attendance/sync-all'),
  },
  admin: {
    users: () => fetchJSON('/admin/users'),
    invites: () => fetchJSON('/admin/invites'),
    deleteInviteByEmail: (email) => deleteJSON(`/admin/invites/by-email/${encodeURIComponent(email)}`),
    updateUser: (userId, data) => patchJSON(`/admin/users/${userId}`, data),
    setUserPassword: (userId, password) => patchJSON(`/admin/users/${userId}/password`, { password }),
    impersonateUser: (userId) => postJSON(`/admin/users/${userId}/impersonate`),
    deleteUser: (userId) => deleteJSON(`/admin/users/${userId}`),
    userClinics: (userId) => fetchJSON(`/admin/users/${userId}/clinics`),
    clinics: () => fetchJSON('/admin/clinics'),
    clinic: (clinicId) => fetchJSON(`/admin/clinics/${clinicId}`),
    createClinic: (data) => postJSON('/admin/clinics', data),
    updateClinic: (clinicId, data) => patchJSON(`/admin/clinics/${clinicId}`, data),
    deleteClinic: (clinicId) => deleteJSON(`/admin/clinics/${clinicId}`),
    addClinicMember: (clinicId, userId) => postJSON(`/admin/clinics/${clinicId}/members`, { userId }),
    removeClinicMember: (clinicId, userId) => deleteJSON(`/admin/clinics/${clinicId}/members/${userId}`),
    clinicians: () => fetchJSON('/admin/clinicians'),
    clinician: (clinicianId) => fetchJSON(`/admin/clinicians/${clinicianId}`),
    updateClinician: (clinicianId, data) => patchJSON(`/admin/clinicians/${clinicianId}`, data),
    updateClinicianRecord: (clinicianId, recordId, data) => patchJSON(`/admin/clinicians/${clinicianId}/records/${recordId}`, data),
    settings: () => fetchJSON('/admin/settings'),
    updateSettings: (data) => patchJSON('/admin/settings', data),
    previewMonthlySummary: () => postJSON('/admin/monthly-summary/preview'),
    syncStatus: () => fetchJSON('/admin/sync-status'),
  },
};
